import React, { forwardRef, useRef, useState, useEffect } from "react";
import styled from "styled-components";
import classNames from "classnames";
import Dropdown from 'react-bootstrap/Dropdown';
import { DropdownProvider } from "./DropdownContext";
import DropdownMenu from "./DropdownMenu";
import DropdownToggle from "./DropdownToggle";
import IBuildrProps from "../../../utilities/IBuildrProps";
import { SelectCallback } from "react-bootstrap/esm/helpers";
import { AutomationTestIdFeature } from "../../../utilities/automation";

enum ArrowDirection {
    Up,
    Down,
    Other,
}

export interface IDropdownProps extends IBuildrProps {
    id?: string;
    name?: string;
    title?: string;
    required?: boolean;
    className?: string;
    inputClassName?: string;
    hintText?: string;
    onChange?: SelectCallback;
    defaultValue?: string;
    autoSelect?: boolean;
    isWhite?: boolean;
    visibleItems?: string;
    feature?: AutomationTestIdFeature;
    children;
}

export default styled(forwardRef(function(props: IDropdownProps, ref: any) {

    const [open, setOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>();
    const defaultSelectedItem = getSelectedItem();
    const onChange = props.onChange || (() => {});

    useEffect(() => {
        if (defaultSelectedItem) {
            onChange(defaultSelectedItem.eventKey || defaultSelectedItem.text, null);
        }
    }, [defaultSelectedItem.eventKey, defaultSelectedItem.text, defaultSelectedItem.html]);

    function getSelectedItem() {
        const children = props.children || [];

        // Checks for the selected default item
        let selectedItem = children.find(item => item.props.selected);

        // If enabled it autoselects the first item if there is not one already selected
        selectedItem = props.autoSelect && !selectedItem ? children[0] : selectedItem;
        const html = selectedItem?.props?.html;
        const text = selectedItem?.props?.text || '';
        const eventKey = selectedItem?.props?.eventKey || text;
        return { eventKey, text, html };
    }

    function getArrowDirection(key) {
        if (key === "ArrowDown") {
            return ArrowDirection.Down;
        } else if (key === "ArrowUp") {
            return ArrowDirection.Up
        } else {
            return ArrowDirection.Other;
        }
    }
    
    function navigateMenu(event) {
        let key = event.key;
        let arrowDirection: ArrowDirection = getArrowDirection(key);
        const items: HTMLAnchorElement[] = menuRef.current ? Array.from(menuRef.current.querySelectorAll('a.custom-dropdown-item:not(.disabled):not(.placeholder)')) : [];

        if (items.length === 0) return;

        const activeItem = items.find(item => item === document.activeElement);
        const activeIndex = items.indexOf(activeItem);
        let nextIndex = 0;

        // Handle up and down arrow keys
        if (arrowDirection !== ArrowDirection.Other) {
            if (activeIndex !== -1) {
                if (arrowDirection === ArrowDirection.Up) {
                    nextIndex = Math.max(0, activeIndex - 1);
                } else {
                    nextIndex = Math.min(items.length - 1, activeIndex + 1);
                }
            }

            items[nextIndex]?.focus();

        // Handle alphanumeric keys
        } else if (/^[a-zA-Z0-9]$/.test(key)) {
            key = key.toLowerCase();
            const foundItems = items.filter(item => item.innerText.toLowerCase().startsWith(key));

            if (foundItems.length === 0) return;
            
            const foundActiveIndex = foundItems.indexOf(activeItem);

            if(foundActiveIndex + 1 === foundItems.length) {
                // keep nextIndex = 0
            } else if (foundActiveIndex !== -1 && activeItem.innerText.toLowerCase().startsWith(key)) {
                nextIndex = Math.min(foundItems.length - 1, foundActiveIndex + 1);
            }
            
            foundItems[nextIndex]?.focus();
        }
    }

    function toggleDropdown(open) {
        document.body.classList.toggle('no-scroll-mobile', open);
        setOpen(open);
    }

    return (
        <div data-id={props.cmsId} ref={ref} className={classNames('mb-3', props.className, props.editorClassObj)}>
            <div className="form-group" ref={menuRef}>
                {open ? <Backdrop /> : null}
                <DropdownProvider defaultSelectedItem={defaultSelectedItem} open={open}>
                    <Dropdown onToggle={toggleDropdown} onKeyDown={navigateMenu} onSelect={onChange}>
                        <DropdownToggle id={props.id} title={props.title} name={props.name || ""} required={props.required} inputClassName={props.inputClassName} isWhite={props.isWhite} feature={props.feature} />
                        <DropdownMenu title={props.title} visibleItems={Number(props.visibleItems)}>{props.children}</DropdownMenu>
                    </Dropdown>
                </DropdownProvider>
                {props.hintText ? <div><small>{props.hintText}</small></div> : null}
            </div>
        </div>
    );
}))`
    .custom-dropdown-menu {
        color: var(--black, #2E2E2B);
        width: 100%;
        border-radius: 2px;
        background: #FFF;
        box-shadow: 0px 8px 50px 0px rgba(0, 0, 0, 0.25);
        transform: translate3d(0px, 0px, 0px) !important;

        @media (max-width:992px) {
            position: fixed!important;
            inset: 50% 5vw 0 !important;
            transform: translateY(-50%) !important;
            width: 90vw;
            z-index: 1056!important;
            display: flex;
            flex-direction: column;
        }
    }
`;

const Backdrop = styled(({ className = '' }) => {
    return (
        <>
            <div className={classNames(className, 'modal-backdrop modal-backdrop-glass d-block d-lg-none')} />
            <div className={classNames(className, 'white-bg d-block d-lg-none')} />
        </>
    );
})`
    height: 100vh;
    z-index: 1056!important;
    &.white-bg {
        left: 0;
        position: fixed;
        top: 0;
        width: 100vw;
        z-index: 1053;
        background: rgba(236, 234, 233, 0.85);
    }
`;